import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FiMapPin, FiRefreshCw, FiSend, FiSlash } from "react-icons/fi";

const formatName = (user) =>
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "Unknown";

const formatDate = (value) => value ? new Date(value).toLocaleString() : "Never";

const linkUrl = (token) => `${window.location.origin}/attendance-test/${token}`;

const mapUrl = (event) => event?.latitude != null && event?.longitude != null
  ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${event.latitude},${event.longitude}`)}`
  : null;

export default function AdminAttendancePilot() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    userId: "",
    label: "",
    notificationRecipients: "",
    expiresAt: "",
    sendEmail: true,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/attendance-pilot/admin-data", { credentials: "include" });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to load pilot data");
      setData(result);
      if (!form.userId && result.users?.length) {
        setForm((current) => ({ ...current, userId: String(result.users[0].id) }));
      }
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [form.userId]);

  useEffect(() => {
    loadData();
  }, []);

  const selectedUser = useMemo(
    () => data?.users?.find((user) => String(user.id) === String(form.userId)),
    [data?.users, form.userId]
  );

  const usersById = useMemo(
    () => new Map((data?.users || []).map((user) => [String(user.id), user])),
    [data?.users]
  );

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitLink = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await fetch("/attendance-pilot/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action: "create",
          userId: form.userId,
          label: form.label || undefined,
          notificationRecipients: form.notificationRecipients,
          expiresAt: form.expiresAt || undefined,
          institution: selectedUser?.institution || data?.institution || undefined,
          sendEmail: form.sendEmail,
          adminActionToken: data?.adminActionToken,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to create link");
      toast({
        title: "Pilot link ready",
        description: result.link?.url || "Link created.",
        status: "success",
        duration: 5000,
      });
      await loadData();
    } catch (saveError) {
      toast({ title: "Link failed", description: saveError.message, status: "error", duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const linkAction = async (action, linkId) => {
    setSaving(true);
    try {
      const response = await fetch("/attendance-pilot/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action, linkId, adminActionToken: data?.adminActionToken }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || `Unable to ${action} link`);
      toast({ title: action === "disable" ? "Link disabled" : "Link resent", status: "success", duration: 3500 });
      await loadData();
    } catch (actionError) {
      toast({ title: "Action failed", description: actionError.message, status: "error", duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const backfillGeocoding = async () => {
    setGeocoding(true);
    try {
      const response = await fetch("/attendance-pilot/geocode-sites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          institution: data?.institution || selectedUser?.institution || undefined,
          adminActionToken: data?.adminActionToken,
          limit: 100,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || "Unable to geocode sites");
      const geocoded = result.counts?.geocoded || 0;
      const failed = result.counts?.failed || 0;
      const noAddress = result.counts?.no_address || 0;
      toast({
        title: "Geocoding complete",
        description: `${geocoded} geocoded, ${failed} failed, ${noAddress} missing address.`,
        status: failed ? "warning" : "success",
        duration: 6000,
      });
      await loadData();
    } catch (geocodeError) {
      toast({ title: "Geocoding failed", description: geocodeError.message, status: "error", duration: 6000 });
    } finally {
      setGeocoding(false);
    }
  };

  if (loading && !data) {
    return (
      <Flex minH="60vh" align="center" justify="center" color="white">
        <Spinner />
      </Flex>
    );
  }

  return (
    <Box bg="gray.950" color="white" minH="100vh" p={{ base: 4, md: 6 }}>
      <Flex justify="space-between" align="center" gap={4} mb={6} flexWrap="wrap">
        <Box>
          <Heading size="lg">Attendance Geolocation Pilot</Heading>
          <Text color="gray.400" mt={1}>
            Test links, evidence events, site matching, and IP rollups.
          </Text>
        </Box>
        <Button leftIcon={<FiRefreshCw />} onClick={loadData} isLoading={loading} size="sm">
          Refresh
        </Button>
      </Flex>

      {error && (
        <Alert status="error" mb={5} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box as="form" onSubmit={submitLink} bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" p={4} mb={6}>
        <Heading size="md" mb={4}>Create Test Link</Heading>
        <Flex gap={4} flexWrap="wrap">
          <FormControl minW={{ base: "100%", md: "260px" }} flex="1" isRequired>
            <FormLabel>User</FormLabel>
            <Select value={form.userId} onChange={(event) => updateForm("userId", event.target.value)} bg="gray.800" borderColor="gray.600">
              {(data?.users || []).map((user) => (
                <option key={user.id} value={String(user.id)}>
                  {formatName(user)} ({user.email})
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl minW={{ base: "100%", md: "220px" }} flex="1">
            <FormLabel>Label</FormLabel>
            <Input value={form.label} onChange={(event) => updateForm("label", event.target.value)} bg="gray.800" borderColor="gray.600" placeholder="Memphis pilot" />
          </FormControl>
          <FormControl minW={{ base: "100%", md: "220px" }} flex="1">
            <FormLabel>Expires At</FormLabel>
            <Input type="datetime-local" value={form.expiresAt} onChange={(event) => updateForm("expiresAt", event.target.value)} bg="gray.800" borderColor="gray.600" />
          </FormControl>
        </Flex>
        <FormControl mt={4}>
          <FormLabel>Notification Recipients</FormLabel>
          <Textarea value={form.notificationRecipients} onChange={(event) => updateForm("notificationRecipients", event.target.value)} bg="gray.800" borderColor="gray.600" rows={2} placeholder="email@example.edu, coordinator@example.edu" />
        </FormControl>
        <HStack mt={4} justify="space-between" align="center" flexWrap="wrap">
          <Button
            size="sm"
            variant={form.sendEmail ? "solid" : "outline"}
            colorScheme={form.sendEmail ? "blue" : "gray"}
            onClick={() => updateForm("sendEmail", !form.sendEmail)}
          >
            {form.sendEmail ? "Send email enabled" : "Send email disabled"}
          </Button>
          <HStack>
            <Button size="sm" leftIcon={<FiMapPin />} onClick={backfillGeocoding} isLoading={geocoding} variant="outline">
              Geocode Missing Sites
            </Button>
            <Button type="submit" leftIcon={<FiSend />} colorScheme="blue" isLoading={saving}>
              Create Link
            </Button>
          </HStack>
        </HStack>
      </Box>

      <VStack spacing={6} align="stretch">
        <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" overflowX="auto">
          <Box p={4} borderBottom="1px solid" borderColor="gray.700">
            <Heading size="md">Reusable Links</Heading>
          </Box>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th color="gray.300">Assigned User</Th>
                <Th color="gray.300">Status</Th>
                <Th color="gray.300">Last Used</Th>
                <Th color="gray.300">Link</Th>
                <Th color="gray.300">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(data?.links || []).map((link) => {
                const assignedUser = link.assignedUser?.id || link.assignedUser?.email
                  ? link.assignedUser
                  : usersById.get(String(link.assignedUserId || ""));
                const assignedUserName = assignedUser ? formatName(assignedUser) : link.label || "Unknown";
                const assignedUserEmail = assignedUser?.email || null;
                return (
                  <Tr key={link.id}>
                    <Td>{assignedUserName}<Text color="gray.500" fontSize="xs">{assignedUserEmail}</Text></Td>
                    <Td><Badge colorScheme={link.enabled ? "green" : "red"}>{link.enabled ? "Enabled" : "Disabled"}</Badge></Td>
                    <Td>{formatDate(link.lastUsedAt)}</Td>
                    <Td maxW="320px"><Text fontSize="xs" color="blue.200" noOfLines={1}>{linkUrl(link.token)}</Text></Td>
                    <Td>
                      <HStack>
                        <Button size="xs" onClick={() => navigator.clipboard?.writeText(linkUrl(link.token))}>Copy</Button>
                        <Button size="xs" leftIcon={<FiSend />} onClick={() => linkAction("resend", link.id)} isDisabled={saving}>Resend</Button>
                        <Button size="xs" leftIcon={<FiSlash />} colorScheme="red" variant="outline" onClick={() => linkAction("disable", link.id)} isDisabled={saving || !link.enabled}>Disable</Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" overflowX="auto">
          <Box p={4} borderBottom="1px solid" borderColor="gray.700">
            <Heading size="md">Recent Test Events</Heading>
          </Box>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th color="gray.300">Time</Th>
                <Th color="gray.300">User</Th>
                <Th color="gray.300">Permission</Th>
                <Th color="gray.300">Site Match</Th>
                <Th color="gray.300">Nearest</Th>
                <Th color="gray.300">Coordinates</Th>
                <Th color="gray.300">IP</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(data?.events || []).map((event) => (
                <Tr key={event.id}>
                  <Td>{formatDate(event.occurredAt)}</Td>
                  <Td>{formatName(event.user)}</Td>
                  <Td>
                    <Badge colorScheme={event.locationPermissionState === "granted" ? "green" : event.locationPermissionState === "denied" ? "red" : "yellow"}>
                      {event.locationPermissionState || "unknown"}
                    </Badge>
                    {event.locationFailureReason && <Text color="gray.500" fontSize="xs">{event.locationFailureReason}</Text>}
                  </Td>
                  <Td>{event.siteMatchStatus || "unknown"}</Td>
                  <Td>{event.nearestSitesSnapshot?.[0]?.name || event.matchedSiteSnapshot?.name || "No coordinates"}</Td>
                  <Td>
                    {mapUrl(event) ? (
                      <Button as="a" href={mapUrl(event)} target="_blank" rel="noreferrer" size="xs" variant="outline">
                        Map Pin
                      </Button>
                    ) : (
                      <Text color="gray.500" fontSize="xs">None</Text>
                    )}
                  </Td>
                  <Td>{event.requestIp || "Unknown"}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Box bg="gray.900" border="1px solid" borderColor="gray.700" borderRadius="md" overflowX="auto">
          <Box p={4} borderBottom="1px solid" borderColor="gray.700">
            <Heading size="md">IP History</Heading>
          </Box>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th color="gray.300">User</Th>
                <Th color="gray.300">IP</Th>
                <Th color="gray.300">Count</Th>
                <Th color="gray.300">First Seen</Th>
                <Th color="gray.300">Last Seen</Th>
                <Th color="gray.300">Modes</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(data?.ipProfiles || []).map((profile) => (
                <Tr key={profile.id}>
                  <Td>{formatName(profile.user)}</Td>
                  <Td>{profile.ipAddress}</Td>
                  <Td>{profile.eventCount}</Td>
                  <Td>{formatDate(profile.firstSeenAt)}</Td>
                  <Td>{formatDate(profile.lastSeenAt)}</Td>
                  <Td>{(profile.sourceModes || []).join(", ")}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>
    </Box>
  );
}
